import { Box, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { useOrderStore } from 'lib/hooks'
import { useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { getIssues, Order, OrderArticle } from 'shared'

interface ReportIssueDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (problem: string, code: string) => void
  type: string
  order: Order | undefined
  editMode?: boolean
}

export const ReportIssueDialog = ({
  open,
  onClose,
  onConfirm,
  type,
  order,
  editMode = false,
}: ReportIssueDialogProps): JSX.Element => {
  const { t } = useTranslation()

  const selectedArticle = useOrderStore((state) => state.selectedArticle)

  const articleIssueTypes = [
    'missingArticle',
    'wrongCondition',
    'wrongLanguage',
    'wrongFoil',
    'wrongFirstEd',
    'other',
  ]
  const [issueKey, setIssueKey] = useState<string>(articleIssueTypes[0])
  const issueCode = type === 'article' ? issueKey : ''
  const [issueText, setIssueText] = useState<string | undefined>(
    t(`orders.issueTypes.${articleIssueTypes[0]}`)
  )
  const orderIssueTypes: string[] = []

  const handleChangeSelect = (e: any) => {
    setIssueKey(e.target.value)
    setIssueText(t(`orders.issueTypes.${e.target.value}`))
  }

  const handleChangeTextField = (e: any) => {
    setIssueText(e.target.value)
  }

  useEffect(() => {
    if (editMode) {
      setIssueText(order?.issues?.[0].description)
    }
  }, [editMode])

  const getArticleIssueLabel = (article: OrderArticle | undefined) => {
    let articleIssueLabel = ''
    if (article) {
      if (article.card.expansion) {
        articleIssueLabel = `"${article.card.name}" from "${article.card.expansion}"`
      } else {
        articleIssueLabel = article.card.name
      }
    }
    return articleIssueLabel
  }

  const showCurrentIssues = () => {
    if (type === 'article') {
      return (
        <>
          <Box padding={1} textAlign="center">
            <Typography variant="body1">Current issues</Typography>
          </Box>
          {selectedArticle?.issues[0].description}
        </>
      )
    } else if (type === 'order') {
      const articleIssues: string[] = []
      order?.articles?.forEach((pta) => {
        // const article = order.article.find((a) => a.idArticle === pta.idArticle)
        pta.issues?.forEach((i) => {
          const articleLabel = getArticleIssueLabel(pta)
          articleIssues.push(`${articleLabel}: ${i.description}`)
        })
      })

      return (
        <>
          <Box padding={1} textAlign="center">
            <Typography variant="body1">Current issues</Typography>
          </Box>
          {!editMode ? (
            <>
              {order?.issues?.map((i) => (
                <Box key={i.description}>
                  {i.description}
                  {i.solved ? ` (SOLVED: ${i.solution})` : ''}
                </Box>
              ))}
              {articleIssues.map((i) => (
                <Box key={i}>{i}</Box>
              ))}
            </>
          ) : (
            <>
              <TextField
                fullWidth
                variant="outlined"
                value={issueText}
                onChange={handleChangeTextField}
                data-testid="report-issue-description"
              ></TextField>
            </>
          )}
        </>
      )
    }
  }

  const getDialogBody = () => {
    if (type === 'article') {
      return (
        <>
          <Box display="flex">
            <RadioGroup value={issueKey} onChange={handleChangeSelect}>
              {articleIssueTypes.map((p) => {
                return (
                  <FormControlLabel
                    key={p}
                    value={p}
                    control={<Radio />}
                    label={t(`orders.issueTypes.${p}`)}
                    data-testid={p}
                  />
                )
              })}
            </RadioGroup>
          </Box>
          {issueKey === 'other' && (
            <Box display="flex">
              <TextField fullWidth variant="outlined" onChange={handleChangeTextField}></TextField>
            </Box>
          )}
        </>
      )
    } else if (type === 'order') {
      return (
        <>
          <Box display="flex">
            <RadioGroup value={issueKey} onChange={handleChangeSelect}>
              {orderIssueTypes.map((p) => {
                return (
                  <FormControlLabel
                    key={p}
                    value={p}
                    control={<Radio />}
                    label={t(`orders.issueTypes.${p}`)}
                  />
                )
              })}
            </RadioGroup>
          </Box>
          <Box display="flex">
            <TextField fullWidth variant="outlined" onChange={handleChangeTextField} />
          </Box>
        </>
      )
    }
  }

  const resetFields = () => {
    setIssueKey(articleIssueTypes[0])
    setIssueText(t(`orders.issueTypes.${articleIssueTypes[0]}`))
  }

  const handleClose = () => {
    resetFields()
    onClose()
  }

  return (
    <ClosableDialog open={open} onClose={handleClose} maxWidth="xl">
      <Box padding={{ xs: 2, sm: 4 }}>
        {(order && getIssues(order).length > 0) ||
        (selectedArticle && selectedArticle.issues.length > 0) ? (
          showCurrentIssues()
        ) : (
          <>
            <Box marginBottom={3}>
              <h3>{t('orders.reportDialogTitle')}</h3>
            </Box>
            {getDialogBody()}
            {type === 'article' && issueKey !== 'missingArticle' && (
              <Alert variant="warning" className="mt-3" data-testid="check-other-locations-warning">
                {t('orders.checkOtherLocationsWarn')}
              </Alert>
            )}
            <div className="d-flex flex-wrap justify-content-end gap-3 mt-4">
              <Button variant="outlined" onClick={handleClose}>
                {t('common.close')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  resetFields()
                  if (issueText) {
                    onConfirm(issueText, issueCode)
                  }
                }}
                data-testid={'confirm-issue'}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </>
        )}
      </Box>
    </ClosableDialog>
  )
}
